/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const O = globalThis, L = O.ShadowRoot && (O.ShadyCSS === void 0 || O.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, rt = Symbol(), F = /* @__PURE__ */ new WeakMap();
let ht = class {
  constructor(t, e, i) {
    if (this._$cssResult$ = !0, i !== rt) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (L && t === void 0) {
      const i = e !== void 0 && e.length === 1;
      i && (t = F.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), i && F.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const ct = (r) => new ht(typeof r == "string" ? r : r + "", void 0, rt), pt = (r, t) => {
  if (L) r.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet);
  else for (const e of t) {
    const i = document.createElement("style"), s = O.litNonce;
    s !== void 0 && i.setAttribute("nonce", s), i.textContent = e.cssText, r.appendChild(i);
  }
}, K = L ? (r) => r : (r) => r instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const i of t.cssRules) e += i.cssText;
  return ct(e);
})(r) : r;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: ut, defineProperty: ft, getOwnPropertyDescriptor: mt, getOwnPropertyNames: bt, getOwnPropertySymbols: wt, getPrototypeOf: vt } = Object, H = globalThis, J = H.trustedTypes, yt = J ? J.emptyScript : "", gt = H.reactiveElementPolyfillSupport, E = (r, t) => r, P = { toAttribute(r, t) {
  switch (t) {
    case Boolean:
      r = r ? yt : null;
      break;
    case Object:
    case Array:
      r = r == null ? r : JSON.stringify(r);
  }
  return r;
}, fromAttribute(r, t) {
  let e = r;
  switch (t) {
    case Boolean:
      e = r !== null;
      break;
    case Number:
      e = r === null ? null : Number(r);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(r);
      } catch {
        e = null;
      }
  }
  return e;
} }, V = (r, t) => !ut(r, t), Z = { attribute: !0, type: String, converter: P, reflect: !1, useDefault: !1, hasChanged: V };
Symbol.metadata ??= Symbol("metadata"), H.litPropertyMetadata ??= /* @__PURE__ */ new WeakMap();
class $ extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ??= []).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = Z) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(t) && ((e = Object.create(e)).wrapped = !0), this.elementProperties.set(t, e), !e.noAccessor) {
      const i = Symbol(), s = this.getPropertyDescriptor(t, i, e);
      s !== void 0 && ft(this.prototype, t, s);
    }
  }
  static getPropertyDescriptor(t, e, i) {
    const { get: s, set: n } = mt(this.prototype, t) ?? { get() {
      return this[e];
    }, set(o) {
      this[e] = o;
    } };
    return { get: s, set(o) {
      const l = s?.call(this);
      n?.call(this, o), this.requestUpdate(t, l, i);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? Z;
  }
  static _$Ei() {
    if (this.hasOwnProperty(E("elementProperties"))) return;
    const t = vt(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(E("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(E("properties"))) {
      const e = this.properties, i = [...bt(e), ...wt(e)];
      for (const s of i) this.createProperty(s, e[s]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0) for (const [i, s] of e) this.elementProperties.set(i, s);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, i] of this.elementProperties) {
      const s = this._$Eu(e, i);
      s !== void 0 && this._$Eh.set(s, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const i = new Set(t.flat(1 / 0).reverse());
      for (const s of i) e.unshift(K(s));
    } else t !== void 0 && e.push(K(t));
    return e;
  }
  static _$Eu(t, e) {
    const i = e.attribute;
    return i === !1 ? void 0 : typeof i == "string" ? i : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), this.constructor.l?.forEach((t) => t(this));
  }
  addController(t) {
    (this._$EO ??= /* @__PURE__ */ new Set()).add(t), this.renderRoot !== void 0 && this.isConnected && t.hostConnected?.();
  }
  removeController(t) {
    this._$EO?.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const i of e.keys()) this.hasOwnProperty(i) && (t.set(i, this[i]), delete this[i]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return pt(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    this.renderRoot ??= this.createRenderRoot(), this.enableUpdating(!0), this._$EO?.forEach((t) => t.hostConnected?.());
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    this._$EO?.forEach((t) => t.hostDisconnected?.());
  }
  attributeChangedCallback(t, e, i) {
    this._$AK(t, i);
  }
  _$ET(t, e) {
    const i = this.constructor.elementProperties.get(t), s = this.constructor._$Eu(t, i);
    if (s !== void 0 && i.reflect === !0) {
      const n = (i.converter?.toAttribute !== void 0 ? i.converter : P).toAttribute(e, i.type);
      this._$Em = t, n == null ? this.removeAttribute(s) : this.setAttribute(s, n), this._$Em = null;
    }
  }
  _$AK(t, e) {
    const i = this.constructor, s = i._$Eh.get(t);
    if (s !== void 0 && this._$Em !== s) {
      const n = i.getPropertyOptions(s), o = typeof n.converter == "function" ? { fromAttribute: n.converter } : n.converter?.fromAttribute !== void 0 ? n.converter : P;
      this._$Em = s, this[s] = o.fromAttribute(e, n.type) ?? this._$Ej?.get(s) ?? null, this._$Em = null;
    }
  }
  requestUpdate(t, e, i) {
    if (t !== void 0) {
      const s = this.constructor, n = this[t];
      if (i ??= s.getPropertyOptions(t), !((i.hasChanged ?? V)(n, e) || i.useDefault && i.reflect && n === this._$Ej?.get(t) && !this.hasAttribute(s._$Eu(t, i)))) return;
      this.C(t, e, i);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(t, e, { useDefault: i, reflect: s, wrapped: n }, o) {
    i && !(this._$Ej ??= /* @__PURE__ */ new Map()).has(t) && (this._$Ej.set(t, o ?? e ?? this[t]), n !== !0 || o !== void 0) || (this._$AL.has(t) || (this.hasUpdated || i || (e = void 0), this._$AL.set(t, e)), s === !0 && this._$Em !== t && (this._$Eq ??= /* @__PURE__ */ new Set()).add(t));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ??= this.createRenderRoot(), this._$Ep) {
        for (const [s, n] of this._$Ep) this[s] = n;
        this._$Ep = void 0;
      }
      const i = this.constructor.elementProperties;
      if (i.size > 0) for (const [s, n] of i) {
        const { wrapped: o } = n, l = this[s];
        o !== !0 || this._$AL.has(s) || l === void 0 || this.C(s, void 0, n, l);
      }
    }
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), this._$EO?.forEach((i) => i.hostUpdate?.()), this.update(e)) : this._$EM();
    } catch (i) {
      throw t = !1, this._$EM(), i;
    }
    t && this._$AE(e);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    this._$EO?.forEach((e) => e.hostUpdated?.()), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Eq &&= this._$Eq.forEach((e) => this._$ET(e, this[e])), this._$EM();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
}
$.elementStyles = [], $.shadowRootOptions = { mode: "open" }, $[E("elementProperties")] = /* @__PURE__ */ new Map(), $[E("finalized")] = /* @__PURE__ */ new Map(), gt?.({ ReactiveElement: $ }), (H.reactiveElementVersions ??= []).push("2.1.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const q = globalThis, T = q.trustedTypes, G = T ? T.createPolicy("lit-html", { createHTML: (r) => r }) : void 0, nt = "$lit$", v = `lit$${Math.random().toFixed(9).slice(2)}$`, ot = "?" + v, xt = `<${ot}>`, x = document, C = () => x.createComment(""), I = (r) => r === null || typeof r != "object" && typeof r != "function", j = Array.isArray, $t = (r) => j(r) || typeof r?.[Symbol.iterator] == "function", z = `[ 	
\f\r]`, A = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, Q = /-->/g, X = />/g, y = RegExp(`>|${z}(?:([^\\s"'>=/]+)(${z}*=${z}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), Y = /'/g, tt = /"/g, at = /^(?:script|style|textarea|title)$/i, k = Symbol.for("lit-noChange"), f = Symbol.for("lit-nothing"), et = /* @__PURE__ */ new WeakMap(), g = x.createTreeWalker(x, 129);
function lt(r, t) {
  if (!j(r) || !r.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return G !== void 0 ? G.createHTML(t) : t;
}
const kt = (r, t) => {
  const e = r.length - 1, i = [];
  let s, n = t === 2 ? "<svg>" : t === 3 ? "<math>" : "", o = A;
  for (let l = 0; l < e; l++) {
    const a = r[l];
    let u, m, d = -1, b = 0;
    for (; b < a.length && (o.lastIndex = b, m = o.exec(a), m !== null); ) b = o.lastIndex, o === A ? m[1] === "!--" ? o = Q : m[1] !== void 0 ? o = X : m[2] !== void 0 ? (at.test(m[2]) && (s = RegExp("</" + m[2], "g")), o = y) : m[3] !== void 0 && (o = y) : o === y ? m[0] === ">" ? (o = s ?? A, d = -1) : m[1] === void 0 ? d = -2 : (d = o.lastIndex - m[2].length, u = m[1], o = m[3] === void 0 ? y : m[3] === '"' ? tt : Y) : o === tt || o === Y ? o = y : o === Q || o === X ? o = A : (o = y, s = void 0);
    const w = o === y && r[l + 1].startsWith("/>") ? " " : "";
    n += o === A ? a + xt : d >= 0 ? (i.push(u), a.slice(0, d) + nt + a.slice(d) + v + w) : a + v + (d === -2 ? l : w);
  }
  return [lt(r, n + (r[e] || "<?>") + (t === 2 ? "</svg>" : t === 3 ? "</math>" : "")), i];
};
class M {
  constructor({ strings: t, _$litType$: e }, i) {
    let s;
    this.parts = [];
    let n = 0, o = 0;
    const l = t.length - 1, a = this.parts, [u, m] = kt(t, e);
    if (this.el = M.createElement(u, i), g.currentNode = this.el.content, e === 2 || e === 3) {
      const d = this.el.content.firstChild;
      d.replaceWith(...d.childNodes);
    }
    for (; (s = g.nextNode()) !== null && a.length < l; ) {
      if (s.nodeType === 1) {
        if (s.hasAttributes()) for (const d of s.getAttributeNames()) if (d.endsWith(nt)) {
          const b = m[o++], w = s.getAttribute(d).split(v), U = /([.?@])?(.*)/.exec(b);
          a.push({ type: 1, index: n, name: U[2], strings: w, ctor: U[1] === "." ? At : U[1] === "?" ? Et : U[1] === "@" ? St : B }), s.removeAttribute(d);
        } else d.startsWith(v) && (a.push({ type: 6, index: n }), s.removeAttribute(d));
        if (at.test(s.tagName)) {
          const d = s.textContent.split(v), b = d.length - 1;
          if (b > 0) {
            s.textContent = T ? T.emptyScript : "";
            for (let w = 0; w < b; w++) s.append(d[w], C()), g.nextNode(), a.push({ type: 2, index: ++n });
            s.append(d[b], C());
          }
        }
      } else if (s.nodeType === 8) if (s.data === ot) a.push({ type: 2, index: n });
      else {
        let d = -1;
        for (; (d = s.data.indexOf(v, d + 1)) !== -1; ) a.push({ type: 7, index: n }), d += v.length - 1;
      }
      n++;
    }
  }
  static createElement(t, e) {
    const i = x.createElement("template");
    return i.innerHTML = t, i;
  }
}
function _(r, t, e = r, i) {
  if (t === k) return t;
  let s = i !== void 0 ? e._$Co?.[i] : e._$Cl;
  const n = I(t) ? void 0 : t._$litDirective$;
  return s?.constructor !== n && (s?._$AO?.(!1), n === void 0 ? s = void 0 : (s = new n(r), s._$AT(r, e, i)), i !== void 0 ? (e._$Co ??= [])[i] = s : e._$Cl = s), s !== void 0 && (t = _(r, s._$AS(r, t.values), s, i)), t;
}
class _t {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: e }, parts: i } = this._$AD, s = (t?.creationScope ?? x).importNode(e, !0);
    g.currentNode = s;
    let n = g.nextNode(), o = 0, l = 0, a = i[0];
    for (; a !== void 0; ) {
      if (o === a.index) {
        let u;
        a.type === 2 ? u = new R(n, n.nextSibling, this, t) : a.type === 1 ? u = new a.ctor(n, a.name, a.strings, this, t) : a.type === 6 && (u = new Ct(n, this, t)), this._$AV.push(u), a = i[++l];
      }
      o !== a?.index && (n = g.nextNode(), o++);
    }
    return g.currentNode = x, s;
  }
  p(t) {
    let e = 0;
    for (const i of this._$AV) i !== void 0 && (i.strings !== void 0 ? (i._$AI(t, i, e), e += i.strings.length - 2) : i._$AI(t[e])), e++;
  }
}
class R {
  get _$AU() {
    return this._$AM?._$AU ?? this._$Cv;
  }
  constructor(t, e, i, s) {
    this.type = 2, this._$AH = f, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = i, this.options = s, this._$Cv = s?.isConnected ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && t?.nodeType === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = _(this, t, e), I(t) ? t === f || t == null || t === "" ? (this._$AH !== f && this._$AR(), this._$AH = f) : t !== this._$AH && t !== k && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : $t(t) ? this.k(t) : this._(t);
  }
  O(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  _(t) {
    this._$AH !== f && I(this._$AH) ? this._$AA.nextSibling.data = t : this.T(x.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    const { values: e, _$litType$: i } = t, s = typeof i == "number" ? this._$AC(t) : (i.el === void 0 && (i.el = M.createElement(lt(i.h, i.h[0]), this.options)), i);
    if (this._$AH?._$AD === s) this._$AH.p(e);
    else {
      const n = new _t(s, this), o = n.u(this.options);
      n.p(e), this.T(o), this._$AH = n;
    }
  }
  _$AC(t) {
    let e = et.get(t.strings);
    return e === void 0 && et.set(t.strings, e = new M(t)), e;
  }
  k(t) {
    j(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let i, s = 0;
    for (const n of t) s === e.length ? e.push(i = new R(this.O(C()), this.O(C()), this, this.options)) : i = e[s], i._$AI(n), s++;
    s < e.length && (this._$AR(i && i._$AB.nextSibling, s), e.length = s);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    for (this._$AP?.(!1, !0, e); t && t !== this._$AB; ) {
      const i = t.nextSibling;
      t.remove(), t = i;
    }
  }
  setConnected(t) {
    this._$AM === void 0 && (this._$Cv = t, this._$AP?.(t));
  }
}
class B {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, e, i, s, n) {
    this.type = 1, this._$AH = f, this._$AN = void 0, this.element = t, this.name = e, this._$AM = s, this.options = n, i.length > 2 || i[0] !== "" || i[1] !== "" ? (this._$AH = Array(i.length - 1).fill(new String()), this.strings = i) : this._$AH = f;
  }
  _$AI(t, e = this, i, s) {
    const n = this.strings;
    let o = !1;
    if (n === void 0) t = _(this, t, e, 0), o = !I(t) || t !== this._$AH && t !== k, o && (this._$AH = t);
    else {
      const l = t;
      let a, u;
      for (t = n[0], a = 0; a < n.length - 1; a++) u = _(this, l[i + a], e, a), u === k && (u = this._$AH[a]), o ||= !I(u) || u !== this._$AH[a], u === f ? t = f : t !== f && (t += (u ?? "") + n[a + 1]), this._$AH[a] = u;
    }
    o && !s && this.j(t);
  }
  j(t) {
    t === f ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class At extends B {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === f ? void 0 : t;
  }
}
class Et extends B {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== f);
  }
}
class St extends B {
  constructor(t, e, i, s, n) {
    super(t, e, i, s, n), this.type = 5;
  }
  _$AI(t, e = this) {
    if ((t = _(this, t, e, 0) ?? f) === k) return;
    const i = this._$AH, s = t === f && i !== f || t.capture !== i.capture || t.once !== i.once || t.passive !== i.passive, n = t !== f && (i === f || s);
    s && this.element.removeEventListener(this.name, this, i), n && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    typeof this._$AH == "function" ? this._$AH.call(this.options?.host ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class Ct {
  constructor(t, e, i) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = i;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    _(this, t);
  }
}
const It = q.litHtmlPolyfillSupport;
It?.(M, R), (q.litHtmlVersions ??= []).push("3.3.0");
const Mt = (r, t, e) => {
  const i = e?.renderBefore ?? t;
  let s = i._$litPart$;
  if (s === void 0) {
    const n = e?.renderBefore ?? null;
    i._$litPart$ = s = new R(t.insertBefore(C(), n), n, void 0, e ?? {});
  }
  return s._$AI(r), s;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const W = globalThis;
class S extends $ {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    const t = super.createRenderRoot();
    return this.renderOptions.renderBefore ??= t.firstChild, t;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Mt(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    super.connectedCallback(), this._$Do?.setConnected(!0);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._$Do?.setConnected(!1);
  }
  render() {
    return k;
  }
}
S._$litElement$ = !0, S.finalized = !0, W.litElementHydrateSupport?.({ LitElement: S });
const Rt = W.litElementPolyfillSupport;
Rt?.({ LitElement: S });
(W.litElementVersions ??= []).push("4.2.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ut = { attribute: !0, type: String, converter: P, reflect: !1, hasChanged: V }, Ot = (r = Ut, t, e) => {
  const { kind: i, metadata: s } = e;
  let n = globalThis.litPropertyMetadata.get(s);
  if (n === void 0 && globalThis.litPropertyMetadata.set(s, n = /* @__PURE__ */ new Map()), i === "setter" && ((r = Object.create(r)).wrapped = !0), n.set(e.name, r), i === "accessor") {
    const { name: o } = e;
    return { set(l) {
      const a = t.get.call(this);
      t.set.call(this, l), this.requestUpdate(o, a, r);
    }, init(l) {
      return l !== void 0 && this.C(o, void 0, r, l), l;
    } };
  }
  if (i === "setter") {
    const { name: o } = e;
    return function(l) {
      const a = this[o];
      t.call(this, l), this.requestUpdate(o, a, r);
    };
  }
  throw Error("Unsupported decorator location: " + i);
};
function c(r) {
  return (t, e) => typeof e == "object" ? Ot(r, t, e) : ((i, s, n) => {
    const o = s.hasOwnProperty(n);
    return s.constructor.createProperty(n, i), o ? Object.getOwnPropertyDescriptor(s, n) : void 0;
  })(r, t, e);
}
const Pt = (r) => {
  class t extends r {
    constructor(...i) {
      super(...i), this._attachDeclarativeTemplate();
    }
    /**
     * Attempts to use a declarative shadow DOM template if provided.
     * If used, this bypasses Lit's standard rendering.
     */
    _attachDeclarativeTemplate() {
      const i = this.constructor;
      if (!i.template) return;
      const s = document.createElement("template");
      s.innerHTML = i.template.trim();
      const n = s.content.firstChild;
      if (n && n.nodeName === "TEMPLATE" && n.getAttribute("shadowrootmode") === "open") {
        const o = n, l = this.attachShadow({ mode: "open" }), a = o.content.cloneNode(!0);
        l.appendChild(a), this.__hasDeclarativeShadowRoot = !0;
      }
    }
    /**
     * Override createRenderRoot to prevent Lit's standard rendering
     * if we've already attached a declarative shadow DOM template
     */
    createRenderRoot() {
      return this.__hasDeclarativeShadowRoot ? this.shadowRoot : super.createRenderRoot();
    }
  }
  return t;
}, Tt = (r) => {
  class t extends r {
    connectedCallback() {
      super.connectedCallback(), this._bindDeclarativeEvents();
    }
    /**
     * Finds all elements with @event attributes and binds them to the corresponding methods
     */
    _bindDeclarativeEvents() {
      const i = this.shadowRoot;
      if (!i) return;
      const s = document.createTreeWalker(
        i,
        NodeFilter.SHOW_ELEMENT
      );
      let n = s.nextNode();
      for (; n; )
        Array.from(n.attributes).forEach((o) => {
          if (o.name.startsWith("@")) {
            const l = o.name.slice(1), a = o.value, u = this[a];
            typeof u == "function" ? n.addEventListener(l, u.bind(this)) : console.warn(
              `${this.localName}: no method '${a}' for event '${l}'`
            );
          }
        }), n = s.nextNode();
    }
  }
  return t;
}, Nt = (r) => Tt(Pt(r));
let it = 0;
const D = (r) => (it += 1, `${r}-${it}`), Ht = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.66675 11.9998l5.99995 5.3333L19.3334 6.66644" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="square"/></svg>', Bt = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 7.29317V13.3296m0 1.4436v1.3333M21.3339 12c0 5.1549-4.1789 9.3339-9.3339 9.3339-5.15495.0-9.33386-4.179-9.33386-9.3339.0-5.15495 4.17891-9.33386 9.33386-9.33386 5.155.0 9.3339 4.17891 9.3339 9.33386z" stroke="currentColor" stroke-width="1.5"/></svg>';
var zt = Object.defineProperty, p = (r, t, e, i) => {
  for (var s = void 0, n = r.length - 1, o; n >= 0; n--)
    (o = r[n]) && (s = o(t, e, s) || s);
  return s && zt(t, e, s), s;
};
const dt = `<template shadowrootmode="open"><style>/*! tailwindcss v4.1.11 | MIT License | https://tailwindcss.com */
@layer properties{@supports ((-webkit-hyphens:none) and (not (margin-trim:inline))) or ((-moz-orient:inline) and (not (color:rgb(from red r g b)))){*,:before,:after,::backdrop{--tw-rotate-x:initial;--tw-rotate-y:initial;--tw-rotate-z:initial;--tw-skew-x:initial;--tw-skew-y:initial;--tw-shadow:0 0 #0000;--tw-shadow-color:initial;--tw-shadow-alpha:100%;--tw-inset-shadow:0 0 #0000;--tw-inset-shadow-color:initial;--tw-inset-shadow-alpha:100%;--tw-ring-color:initial;--tw-ring-shadow:0 0 #0000;--tw-inset-ring-color:initial;--tw-inset-ring-shadow:0 0 #0000;--tw-ring-inset:initial;--tw-ring-offset-width:0px;--tw-ring-offset-color:#fff;--tw-ring-offset-shadow:0 0 #0000;--tw-blur:initial;--tw-brightness:initial;--tw-contrast:initial;--tw-grayscale:initial;--tw-hue-rotate:initial;--tw-invert:initial;--tw-opacity:initial;--tw-saturate:initial;--tw-sepia:initial;--tw-drop-shadow:initial;--tw-drop-shadow-color:initial;--tw-drop-shadow-alpha:100%;--tw-drop-shadow-size:initial;--tw-border-style:solid;--tw-outline-style:solid}}}.sr-only{clip:rect(0,0,0,0);white-space:nowrap;border-width:0;width:1px;height:1px;margin:-1px;padding:0;position:absolute;overflow:hidden}.static{position:static}.isolate{isolation:isolate}.container{width:100%}@media (min-width:319px){.container{max-width:319px}}@media (min-width:320px){.container{max-width:320px}}@media (min-width:359px){.container{max-width:359px}}@media (min-width:360px){.container{max-width:360px}}@media (min-width:374px){.container{max-width:374px}}@media (min-width:375px){.container{max-width:375px}}@media (min-width:559px){.container{max-width:559px}}@media (min-width:560px){.container{max-width:560px}}@media (min-width:759px){.container{max-width:759px}}@media (min-width:760px){.container{max-width:760px}}@media (min-width:959px){.container{max-width:959px}}@media (min-width:960px){.container{max-width:960px}}@media (min-width:1159px){.container{max-width:1159px}}@media (min-width:1160px){.container{max-width:1160px}}@media (min-width:1359px){.container{max-width:1359px}}@media (min-width:1360px){.container{max-width:1360px}}@media (min-width:1659px){.container{max-width:1659px}}@media (min-width:1660px){.container{max-width:1660px}}@media (min-width:1919px){.container{max-width:1919px}}@media (min-width:1920px){.container{max-width:1920px}}@media (min-width:40rem){.container{max-width:40rem}}@media (min-width:48rem){.container{max-width:48rem}}@media (min-width:64rem){.container{max-width:64rem}}@media (min-width:80rem){.container{max-width:80rem}}@media (min-width:96rem){.container{max-width:96rem}}.m-0{margin:var(--bdk-space-0,0rem)}.mb-2{margin-bottom:var(--bdk-space-2,1.33333rem)}.ml-8{margin-left:var(--bdk-space-8,5.33333rem)}.block{display:block}.contents{display:contents}.flex{display:flex}.hidden{display:none}.inline{display:inline}.h-full{height:100%}.w-full{width:100%}.max-w-\\[500px\\]{max-width:500px}.transform{transform:var(--tw-rotate-x,)var(--tw-rotate-y,)var(--tw-rotate-z,)var(--tw-skew-x,)var(--tw-skew-y,)}.flex-col{flex-direction:column}.items-start{align-items:flex-start}.justify-center{justify-content:center}.gap-2{gap:var(--bdk-space-2,1.33333rem)}.gap-4{gap:var(--bdk-space-4,2.66667rem)}.overflow-y-auto{overflow-y:auto}.p-0{padding:var(--bdk-space-0,0rem)}.bdk-tw-text-field:has(.bdk-text-field--icons) input{padding-right:var(--bdk-space-11,7.33333rem)}.bdk-tw-text-field:has(*+.bdk-text-field--status-icon) input{padding-right:var(--bdk-space-17,11.3333rem)}.bdk-tw-strong-l{letter-spacing:.1rem;text-transform:inherit;font-family:Eduard Breuninger;font-size:3.33333rem;font-weight:500;line-height:5rem}.bdk-tw-text-field:has(input:disabled) .bdk-text-field--hint{color:var(--bdk-pebble,#e0dfde);outline-color:var(--bdk-pebble,#e0dfde)}.bdk-tw-text-field[valid] .bdk-text-field--hint,.bdk-tw-text-field[valid] .bdk-text-field--status-icon{color:var(--bdk-success,#136233)}.bdk-tw-text-field[invalid] .bdk-text-field--status-icon{color:var(--bdk-error,#b20c16)}.bdk-tw-text-field:has(input:disabled) .bdk-text-field--status-icon{color:var(--bdk-pebble,#e0dfde)}.shadow{--tw-shadow:0 1px 3px 0 var(--tw-shadow-color,#0000001a),0 1px 2px -1px var(--tw-shadow-color,#0000001a);box-shadow:var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)}.blur{--tw-blur:blur(8px);filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}.filter{filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}:host{box-sizing:border-box;width:100%;display:block}:host([hidden]){display:none}:host([inverted][invalid]) .bdk-text-field{margin-inline:calc(var(--bdk-space-2,1.33333rem)*-1);border-radius:var(--bdk-radius-2,1.33333rem);background-color:var(--bdk-error-background,#ffedf4);padding:var(--bdk-space-2,1.33333rem)}.bdk-text-field--container{align-items:center;gap:var(--bdk-space-2,1.33333rem);display:flex;position:relative}.bdk-text-field--label{align-items:center;gap:var(--bdk-space-1,.666667rem);padding-bottom:var(--bdk-space-1,.666667rem);letter-spacing:.066667rem;text-transform:inherit;color:var(--bdk-coal-100,#1a1a1a);font-family:Eduard Breuninger;font-size:2.66667rem;font-weight:400;line-height:4rem;display:flex}:host(:focus-within) .bdk-text-field--label{letter-spacing:.066667rem;text-transform:inherit;font-family:Eduard Breuninger;font-size:2.66667rem;font-weight:500;line-height:4rem}:host(:disabled) .bdk-text-field--label{color:var(--bdk-pebble,#e0dfde);outline-color:var(--bdk-pebble,#e0dfde)}:host(:disabled[inverted]) .bdk-text-field--label{color:var(--bdk-snow-50,#ffffff80)}:host([inverted]) .bdk-text-field--label{color:var(--bdk-snow-100,#fff)}:host([valid]) .bdk-text-field--label{color:var(--bdk-success,#136233)}:host([invalid]) .bdk-text-field--label{color:var(--bdk-error,#b20c16)}:host([hidden-label]) .bdk-text-field--label{clip:rect(0,0,0,0);white-space:nowrap;border-width:0;width:1px;height:1px;margin:-1px;padding:0;position:absolute;overflow:hidden}:host([valid][inverted]) .bdk-text-field--label{color:var(--bdk-snow-100,#fff)}.bdk-text-field--input{box-sizing:border-box;height:var(--bdk-space-11,7.33333rem);border-radius:var(--bdk-radius-2,1.33333rem);border-style:var(--tw-border-style);width:100%;padding-inline:var(--bdk-space-3,2rem);letter-spacing:.066667rem;text-transform:inherit;color:var(--bdk-coal-100,#1a1a1a);outline-style:var(--tw-outline-style);outline-offset:calc(2px*-1);outline-width:1px;outline-color:var(--bdk-smoke,#928d8b);--tw-outline-style:solid;border-width:0;outline-style:solid;font-family:Eduard Breuninger;font-size:2.66667rem;font-weight:500;line-height:4rem}.bdk-text-field--input::-moz-placeholder{letter-spacing:.066667rem;text-transform:inherit;color:var(--bdk-ash,#6c6765);font-family:Eduard Breuninger;font-size:2.66667rem;font-weight:400;line-height:4rem}.bdk-text-field--input::placeholder{letter-spacing:.066667rem;text-transform:inherit;color:var(--bdk-ash,#6c6765);font-family:Eduard Breuninger;font-size:2.66667rem;font-weight:400;line-height:4rem}.bdk-text-field--input:not(:disabled):where(:focus,:active){outline-style:var(--tw-outline-style);outline-width:2px;outline-color:var(--bdk-coal-100,#1a1a1a);--tw-outline-style:solid;outline-style:solid}@media screen and (pointer:fine){.bdk-text-field--input:not(:disabled):where(:hover){outline-style:var(--tw-outline-style);outline-width:2px;outline-color:var(--bdk-coal-100,#1a1a1a);--tw-outline-style:solid;outline-style:solid}}.bdk-text-field--input:disabled{background-color:var(--bdk-snow-100,#fff);color:var(--bdk-pebble,#e0dfde);outline-color:var(--bdk-pebble,#e0dfde)}.bdk-text-field--input:disabled::-moz-placeholder{color:var(--bdk-pebble,#e0dfde)}.bdk-text-field--input:disabled::placeholder{color:var(--bdk-pebble,#e0dfde)}:host([inverted]) .bdk-text-field--input{outline-offset:calc(1px*-1)}:host([valid]) .bdk-text-field--input{padding-right:var(--bdk-space-11,7.33333rem);outline-color:var(--bdk-success,#136233)}:host([invalid]) .bdk-text-field--input{padding-right:var(--bdk-space-11,7.33333rem);outline-color:var(--bdk-error,#b20c16)}:host([readonly]) .bdk-text-field--input{--tw-border-style:none;height:auto;padding:var(--bdk-space-0,0rem);--tw-outline-style:none;background-color:#0000;border-style:none;outline-style:none}:host([readonly][inverted]) .bdk-text-field--input{color:var(--bdk-snow-100,#fff)}.bdk-text-field--container[with-custom-icon] .bdk-text-field--input{padding-right:var(--bdk-space-11,7.33333rem)}.bdk-text-field--container[with-custom-icon]:has(.bdk-text-field--status-icon) .bdk-text-field--input{padding-right:var(--bdk-space-17,11.3333rem)}.bdk-text-field--container[with-custom-icon-start] .bdk-text-field--input{padding-left:var(--bdk-space-11,7.33333rem)}.bdk-text-field--icons,.bdk-text-field--icons-start{align-items:center;gap:var(--bdk-space-1,.666667rem);color:var(--bdk-coal-100,#1a1a1a);display:flex;position:absolute}.bdk-text-field--icons{right:var(--bdk-space-3,2rem)}.bdk-text-field--icons-start{left:var(--bdk-space-3,2rem)}.bdk-text-field--status-icon{aspect-ratio:1;height:var(--bdk-space-6,4rem)}:host([valid]) .bdk-text-field--status-icon{color:var(--bdk-success,#136233)}:host([invalid]) .bdk-text-field--status-icon{color:var(--bdk-error,#b20c16)}:host(:disabled) .bdk-text-field--status-icon{color:var(--bdk-pebble,#e0dfde)}.bdk-text-field--error,.bdk-text-field--hint{margin-top:var(--bdk-space-1,.666667rem);letter-spacing:.066667rem;text-transform:inherit;font-family:Eduard Breuninger;font-size:2.66667rem;font-weight:500;line-height:4rem}.bdk-text-field--error{color:var(--bdk-error,#b20c16)}.bdk-text-field--hint{color:var(--bdk-coal-100,#1a1a1a)}:host([inverted]) .bdk-text-field--hint{color:var(--bdk-snow-100,#fff)}:host([valid]) .bdk-text-field--hint{color:var(--bdk-success,#136233)}:host([valid][inverted]) .bdk-text-field--hint{color:var(--bdk-snow-100,#fff)}:host(:disabled) .bdk-text-field--hint{color:var(--bdk-pebble,#e0dfde)}:host(:disabled[inverted]) .bdk-text-field--hint{color:var(--bdk-snow-50,#ffffff80)}@property --tw-rotate-x{syntax:"*";inherits:false}@property --tw-rotate-y{syntax:"*";inherits:false}@property --tw-rotate-z{syntax:"*";inherits:false}@property --tw-skew-x{syntax:"*";inherits:false}@property --tw-skew-y{syntax:"*";inherits:false}@property --tw-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-shadow-color{syntax:"*";inherits:false}@property --tw-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-inset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-shadow-color{syntax:"*";inherits:false}@property --tw-inset-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-ring-color{syntax:"*";inherits:false}@property --tw-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-ring-color{syntax:"*";inherits:false}@property --tw-inset-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-ring-inset{syntax:"*";inherits:false}@property --tw-ring-offset-width{syntax:"<length>";inherits:false;initial-value:0}@property --tw-ring-offset-color{syntax:"*";inherits:false;initial-value:#fff}@property --tw-ring-offset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-blur{syntax:"*";inherits:false}@property --tw-brightness{syntax:"*";inherits:false}@property --tw-contrast{syntax:"*";inherits:false}@property --tw-grayscale{syntax:"*";inherits:false}@property --tw-hue-rotate{syntax:"*";inherits:false}@property --tw-invert{syntax:"*";inherits:false}@property --tw-opacity{syntax:"*";inherits:false}@property --tw-saturate{syntax:"*";inherits:false}@property --tw-sepia{syntax:"*";inherits:false}@property --tw-drop-shadow{syntax:"*";inherits:false}@property --tw-drop-shadow-color{syntax:"*";inherits:false}@property --tw-drop-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-drop-shadow-size{syntax:"*";inherits:false}@property --tw-border-style{syntax:"*";inherits:false;initial-value:solid}@property --tw-outline-style{syntax:"*";inherits:false;initial-value:solid}
</style>
  
  <div class="bdk-text-field">
    <label class="bdk-text-field--label">
      <slot></slot>
    </label>

    <div class="bdk-text-field--container">
      <div class="bdk-text-field--icons-start">
        <slot name="custom-icon-start"></slot>
      </div>
      <input class="bdk-text-field--input" data-bdk-text-field type="text" />
      <div class="bdk-text-field--icons">
        <slot name="custom-icon"></slot>
      </div>
    </div>

    <div class="bdk-text-field--hint">
      <slot name="hint"></slot>
    </div>

    <div class="bdk-text-field--error" aria-live="assertive">
      <slot name="error"></slot>
    </div>
  </div>
</template>`;
function Lt() {
  return dt;
}
const st = {
  check24: Ht,
  warning24: Bt
}, N = class N extends Nt(S) {
  constructor() {
    super(...arguments), this.internals = this.attachInternals(), this.customIconSlotChangeCallback = () => {
      this.customIconSlot.assignedElements().length > 0 ? this.container.setAttribute("with-custom-icon", "") : this.container.removeAttribute("with-custom-icon");
    }, this.customIconStartSlotChangeCallback = () => {
      this.customIconStartSlot.assignedElements().length > 0 ? this.container.setAttribute("with-custom-icon-start", "") : this.container.removeAttribute("with-custom-icon-start");
    }, this.hintSlotChangeCallback = () => {
      this.hintSlot.assignedElements().length > 0 ? (this.hint.removeAttribute("hidden"), this.input.setAttribute("aria-describedby", this.hintId)) : (this.hint.setAttribute("hidden", ""), this.input.removeAttribute("aria-describedby"));
    }, this.onKeyUp = (t) => {
      const { form: e } = this.internals;
      e && t.key === "Enter" && e.requestSubmit();
    }, this.hiddenLabel = !1, this.inputId = D("bdk-text-field-input"), this.hintId = D("bdk-text-field-hint"), this.errorId = D("bdk-text-field-error"), this.disabled = !1, this.required = !1, this.readonly = !1, this.spellcheck = !1, this.type = "text", this.value = "";
  }
  get form() {
    return this.internals.form;
  }
  /* Getter seems to work after connecting, @query does not */
  get input() {
    return this.shadowRoot.querySelector("input");
  }
  get label() {
    return this.shadowRoot.querySelector("label");
  }
  get container() {
    return this.shadowRoot.querySelector(
      ".bdk-text-field--container"
    );
  }
  get customIconSlot() {
    return this.shadowRoot.querySelector("slot[name=custom-icon]");
  }
  get customIconStartSlot() {
    return this.shadowRoot.querySelector("slot[name=custom-icon-start]");
  }
  get hint() {
    return this.shadowRoot.querySelector(".bdk-text-field--hint");
  }
  get hintSlot() {
    return this.shadowRoot.querySelector("slot[name=hint]");
  }
  get error() {
    return this.shadowRoot.querySelector(
      ".bdk-text-field--error"
    );
  }
  /**
   * Overrideable getter&setter for the input value to allow deriving implementations
   * to implement custom input behaviour (like InputMasks)
   */
  set inputValue(t) {
    this.input.value = t;
  }
  get inputValue() {
    return this.input.value;
  }
  firstUpdated(t) {
    super.firstUpdated(t), this.setupEventHandling(), this.customIconSlotChangeCallback(), this.customIconStartSlotChangeCallback(), this.hintSlotChangeCallback(), this.setIds(), this.unsetValidationState();
  }
  updated(t) {
    if (super.updated(t), this._setAttributeIfChanged(t, "type"), this._setAttributeIfChanged(t, "autocomplete"), this._setBooleanAttributeIfChanged(t, "disabled"), this._setAttributeIfChanged(t, "min"), this._setAttributeIfChanged(t, "max"), this._setAttributeIfChanged(t, "minlength"), this._setAttributeIfChanged(t, "maxlength"), this._setAttributeIfChanged(t, "pattern"), this._setAttributeIfChanged(t, "placeholder"), this._setAttributeIfChanged(t, "inputmode"), this._setBooleanAttributeIfChanged(t, "required"), this._setBooleanAttributeIfChanged(t, "readonly"), this._setBooleanAttributeIfChanged(t, "spellcheck"), t.has("value") && (this.inputValue = this.value, this._setFormValueAndValidity()), t.has("type") && this.internals.setValidity && this.internals.setValidity(
      this.input.validity,
      this.input.validationMessage,
      this.input
    ), !(!t.has("invalid") && !t.has("valid"))) {
      if (this.invalid && this.valid)
        throw new Error("Cannot set both invalid and valid attributes");
      this.unsetValidationState(), this.valid && this.handleValidState(), this.invalid && this.handleInvalidState();
    }
  }
  _setFormValueAndValidity() {
    this.internals.setFormValue && this.internals.setFormValue(this.value), this.internals.setValidity && this.internals.setValidity(
      this.input.validity,
      this.input.validationMessage,
      this.input
    );
  }
  _setAttributeIfChanged(t, e) {
    if (!t.has(e)) return;
    const i = this[e];
    i ? this.input.setAttribute(e, i) : this.input.removeAttribute(e);
  }
  _setBooleanAttributeIfChanged(t, e) {
    if (!t.has(e)) return;
    this[e] ? this.input.setAttribute(e, "") : this.input.removeAttribute(e);
  }
  setupEventHandling() {
    this.input.addEventListener("input", () => {
      this.handleInput();
    }), this.input.addEventListener("change", (t) => {
      this.handleChangeEvent(t);
    }), this.input.addEventListener("keyup", this.onKeyUp), this.customIconSlot.addEventListener(
      "slotchange",
      this.customIconSlotChangeCallback
    ), this.customIconStartSlot.addEventListener(
      "slotchange",
      this.customIconStartSlotChangeCallback
    ), this.hintSlot.addEventListener("slotchange", this.hintSlotChangeCallback);
  }
  setIds() {
    this.input.setAttribute("id", this.inputId), this.label.setAttribute("for", this.inputId), this.hint.setAttribute("id", this.hintId), this.error.setAttribute("id", this.errorId);
  }
  unsetValidationState() {
    this.error.setAttribute("hidden", ""), this.removeStatusIcon(), this.input.removeAttribute("aria-invalid"), this.hintSlotChangeCallback();
  }
  handleInvalidState() {
    const t = this.input.getAttribute("aria-describedby");
    this.hint.setAttribute("hidden", ""), this.error.removeAttribute("hidden"), this.input.setAttribute("aria-invalid", "true"), this.addStatusIcon(st.warning24), this.input.setAttribute(
      "aria-describedby",
      [t, this.errorId].join(" ")
    );
  }
  handleValidState() {
    this.addStatusIcon(st.check24);
  }
  addStatusIcon(t) {
    const e = document.createElement("span");
    e.className = "bdk-text-field--status-icon", e.setAttribute("aria-hidden", "true"), e.innerHTML = t;
    const i = this.shadowRoot.querySelector(
      ".bdk-text-field--icons"
    );
    i?.insertBefore(e, i.firstChild);
  }
  removeStatusIcon() {
    this.shadowRoot.querySelector(".bdk-text-field--status-icon")?.remove();
  }
  /* Sets the input value on the <bdk-text-field> via internals for form association and executes browser validation */
  handleInput() {
    this.value = this.inputValue;
  }
  focusInput() {
    this.input.focus();
  }
  /* Needed for search fields with custom buttons */
  resetInput(t) {
    t.preventDefault(), t.stopPropagation(), this.value = "", this.input.focus();
    const e = new InputEvent("input", {
      bubbles: !0,
      composed: !0
    });
    this.input.dispatchEvent(e);
  }
  /* Change event is not bubbling outside the shadow root */
  handleChangeEvent(t) {
    t.preventDefault(), t.stopPropagation();
    const e = new Event("change", { bubbles: !0, composed: !0 });
    this.dispatchEvent(e);
  }
};
N.formAssociated = !0, N.template = dt;
let h = N;
p([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "valid");
p([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "invalid");
p([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "inverted");
p([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "hiddenLabel");
p([
  c({ type: String, attribute: "input-id" })
], h.prototype, "inputId");
p([
  c({ type: String, attribute: "hint-id" })
], h.prototype, "hintId");
p([
  c({ type: String, attribute: "error-id" })
], h.prototype, "errorId");
p([
  c({ reflect: !0 })
], h.prototype, "autocomplete");
p([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "disabled");
p([
  c({ reflect: !0 })
], h.prototype, "min");
p([
  c({ reflect: !0 })
], h.prototype, "max");
p([
  c({ reflect: !0 })
], h.prototype, "name");
p([
  c({ reflect: !0 })
], h.prototype, "minlength");
p([
  c({ reflect: !0 })
], h.prototype, "maxlength");
p([
  c({ reflect: !0 })
], h.prototype, "pattern");
p([
  c({ reflect: !0 })
], h.prototype, "placeholder");
p([
  c({ reflect: !0 })
], h.prototype, "inputmode");
p([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "required");
p([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "readonly");
p([
  c({ type: Boolean, reflect: !0 })
], h.prototype, "spellcheck");
p([
  c({ reflect: !0 })
], h.prototype, "type");
p([
  c({ reflect: !0 })
], h.prototype, "value");
export {
  h as BdkTextField,
  dt as default,
  Lt as dsd
};
